/**
 * Semantic Scene View (SSV): the total, read-only projection of a live Maipo
 * document that agents see (ADR-009 §2). Format "maipo-semantic-scene",
 * version "0.1-draft". This is an editor authoring API, not a format
 * publication; nothing here edits published publications 0.1–0.14.
 *
 * This module has no editor imports.
 */

export const SEMANTIC_SCENE_FORMAT = 'maipo-semantic-scene' as const;
export const SEMANTIC_SCENE_VERSION = '0.1-draft' as const;

/** Native layer id, e.g. "layer_9e8d7c6b-5a4f-4e3d-8c2b-1a0f9e8d7c84". */
export type LayerId = string;
/** Vector object id ("vector_…"); path nodes keep their own ids. */
export type ObjectId = string;
/** Opaque document handle. The daemon maps it to (tabId, sessionId); in-page callers use "local". */
export type DocRef = string;
/** Affine matrix [a, b, c, d, e, f], identical to the editor's core/geometry Matrix. */
export type Matrix = readonly [number, number, number, number, number, number];
export type BlendMode = 'normal' | 'multiply' | 'screen' | 'overlay' | 'darken' | 'lighten' | 'color-dodge'
  | 'color-burn' | 'hard-light' | 'soft-light' | 'difference' | 'exclusion';
export type LabelColor = 'none' | 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'violet' | 'gray';
export interface Rect { x: number; y: number; width: number; height: number }
/**
 * Document-authored text. Data, never instructions. C0/C1 controls and bidi
 * override/isolate characters are escaped as literal `\uXXXX` sequences and the
 * value is length-capped. Every path holding one is listed in `untrustedFields`.
 */
export type ContentString = string;

export type ActorRef =
  | { kind: 'user' }
  | { kind: 'agent'; client: string; via: 'local' | 'remote' }
  | { kind: 'addon'; id: string }
  | { kind: 'replay'; action: string };

/** How an unmapped native feature can be edited. */
export interface FeatureNote {
  /** Extension key (document content when the key is not a known `maipo:` key). */
  key: ContentString;
  summary: string;
  editable: 'semantic' | 'visual-only' | 'preserved-only';
}

export interface DecomposedTransform { x: number; y: number; rotation: number; scaleX: number; scaleY: number; skewX: number }

export interface Paint { type: 'solid'; color: string; alpha: number; swatch?: string; tint?: number }
export interface StrokeView {
  paint: Paint;
  width: number;
  lineCap: 'butt' | 'round' | 'square';
  lineJoin: 'miter' | 'round' | 'bevel';
  dash: number[];
}

export type EffectView =
  | { type: 'dropShadow'; enabled: boolean; color: string; opacity: number; blur: number; offsetX: number; offsetY: number }
  | { type: 'stroke'; enabled: boolean; color: string; opacity: number; width: number }
  | { type: 'smartFilter'; id: string; filter: 'tone' | 'high-pass' | 'displace'; enabled: boolean };

export interface SlotView { name: ContentString; role: 'text' | 'image'; layer: LayerId; prompt?: ContentString }

export interface LayerViewBase {
  id: LayerId;
  kind: 'group' | 'text' | 'image' | 'raster' | 'vector' | 'adjustment';
  name: ContentString;
  parentId: LayerId | null;
  /** Position among siblings; 0 is the top of the layer panel. */
  index: number;
  depth: number;
  visible: boolean;
  opacity: number;
  fillOpacity: number;
  blendMode: BlendMode;
  locks: { all: boolean; pixels: boolean; position: boolean };
  labelColor: LabelColor;
  transform: { matrix: Matrix; decomposed: DecomposedTransform | null };
  bounds: { local: Rect | null; document: Rect | null };
  clipping: boolean;
  mask?: { id: string; kind: 'raster'; enabled: boolean; linked: boolean; defaultAlpha: 0 | 255; coverage?: Rect };
  effects: EffectView[];
  role?: ContentString;
  slot?: SlotView;
  features: FeatureNote[];
  provenance?: { lastTx?: string; lastActor?: ActorRef };
}

export interface TextView {
  engine: 'browser' | 'portable';
  content: ContentString;
  truncated?: boolean;
  box: { width: number; height: number; autoHeight: boolean };
  style: {
    family: string; weight: number; size: number; lineHeight: number; tracking: number;
    align: 'left' | 'center' | 'right'; color: string; swatch?: string;
  };
  missingFont: boolean;
  portable?: { fonts: string[]; baseDirection: 'auto' | 'ltr' | 'rtl'; language: string; overflow: 'allow' | 'reject' };
}

export type VectorGeometryView =
  | { x: number; y: number; width: number; height: number; radius?: [number, number, number, number] }
  | { nodes: number; subpaths: number; fillRule: 'nonzero' | 'evenodd'; detail: 'omitted' };

export interface VectorObjectView {
  id: ObjectId;
  type: 'rectangle' | 'ellipse' | 'path';
  name: ContentString;
  visible: boolean;
  opacity: number;
  transform: Matrix;
  fill: Paint | null;
  stroke: StrokeView | null;
  geometry: VectorGeometryView;
  appearance?: { entries: number };
}

export interface AdjustmentView {
  brightness: number;
  contrast: number;
  saturation: number;
  invert: boolean;
  levels?: { inputBlack: number; inputWhite: number; gamma: number; outputBlack: number; outputWhite: number };
  curves?: { channels: ('rgb' | 'red' | 'green' | 'blue')[] };
  gradientMap?: { enabled: boolean; reverse: boolean; stops: { position: number; color: string }[] };
  blackWhite?: { enabled: boolean; red: number; yellow: number; green: number; cyan: number; blue: number; magenta: number; tint: boolean; tintColor: string };
}

export type GroupLayerView = LayerViewBase & {
  kind: 'group';
  expanded: boolean;
  smartObject?: { embedded: true; sourceLayerCount: number };
  /** Children present in this page, top to bottom. Omitted beyond the requested depth. */
  children?: LayerView[];
  childCount: number;
};
export type TextLayerView = LayerViewBase & { kind: 'text'; text: TextView };
export type ImageLayerView = LayerViewBase & { kind: 'image'; image: { asset: string; frame: { width: number; height: number; fit: 'fill' | 'contain' | 'cover' } } };
export type RasterLayerView = LayerViewBase & { kind: 'raster'; raster: { width: number; height: number; tileSize: number; nonEmptyTiles: number } };
export type VectorLayerView = LayerViewBase & { kind: 'vector'; objects: VectorObjectView[] };
export type AdjustmentLayerView = LayerViewBase & { kind: 'adjustment'; adjustment: AdjustmentView };
export type LayerView = GroupLayerView | TextLayerView | ImageLayerView | RasterLayerView | VectorLayerView | AdjustmentLayerView;

export interface AssetRef {
  id: string;
  mediaType: string;
  byteLength: number;
  width?: number;
  height?: number;
  /** "sha256:…"; omitted over the remote relay (§5). */
  digest?: string;
  usedBy: LayerId[];
  locality: 'embedded';
}

export interface FontRef {
  id?: string;
  family: string;
  weight: number;
  style: 'normal' | 'italic' | 'oblique';
  source: 'embedded' | 'system' | 'installed';
  available: boolean;
  license?: { spdx: string; embedding: 'installable' | 'editable' | 'preview-print' | 'restricted' | 'unknown' };
}

export interface SceneView {
  format: typeof SEMANTIC_SCENE_FORMAT;
  version: typeof SEMANTIC_SCENE_VERSION;
  document: DocRef;
  documentId: string;
  sessionId: string;
  /** DocumentSession.currentRevision (session-scoped; +1 per commit/undo/redo). */
  revision: number;
  savedRevision: number;
  dirty: boolean;
  name: ContentString;
  canvas: { width: number; height: number; ppi: number; colorSpace: 'srgb'; depth: 8 };
  /** Top to bottom, exactly the layer panel's order. */
  layers: LayerView[];
  assets: AssetRef[];
  fonts: FontRef[];
  slots?: SlotView[];
  artboards?: { id: string; name: ContentString; rect: Rect }[];
  swatches?: { id: string; name: ContentString; color: string }[];
  /** Document-level extensions with no semantic mapping (additive to ADR-009 §2). */
  features: FeatureNote[];
  page?: { nextCursor: string; omittedLayers: number };
  /** JSON paths (with `[]` for any array index) whose values are document content. */
  untrustedFields: string[];
}
